import { APP_INITIALIZER, Provider, Type } from '@angular/core';
import { AppInitializerService } from '../services';
import { StoreOrchestratorService } from '../services';
import { CompanyThemeLoaderService } from '@lx/shared/app-config';
import { HostDataReaderService } from '@lx/quote-tool/shared/services';
import {
  QUOTE_DATA_LAYER_STRATEGIES,
  QuoteDataLayerStrategy,
  QuoteToolLiteGoogleAnalyticsDataLayerService,
  UpdatedQuoteTrackingService,
} from '../google-analytics';
import { DATA_LAYER_SERVICE } from '@lx/shared/google-analytics';
import { AdultQuoteDataLayerStrategy } from '../google-analytics/adult-quote-data-layer-strategy.service';
import { JuniorQuoteDataLayerStrategy } from '../google-analytics/junior-quote-data-layer-strategy.service';
import { OnlineQuoteDataLayerStrategy } from '../google-analytics/online-quote-data-layer-strategy.service';
import { AyaQuoteDataLayerStrategy } from '../google-analytics/aya-quote-data-layer-strategy.service';
import { COMPANY } from '@lx/shared/tokens';
import { Company } from '@lx/shared/types';
import { ViewPageTrackingService } from '@lx/quote-tool/shared/google-analytics';

/**
 * Gets the application provider for the company configuration
 * Provides COMPANY token and an APP_INITIALIZER to load company-specific theme CSS.
 *
 * The APP_INITIALIZER performs:
 * 1. Blocks bootstrap until the company-specific theme CSS is fully loaded (network load complete)
 * 2. Company resolution and persistence to sessionStorage is handled at bootstrap time
 *    via main.ts using resolveCompany() and saveCompanyToStorage() functions
 *
 * Bootstrap is blocked via a Promise returned from companyThemeLoaderService.initialize()
 * that resolves only when the theme stylesheet is fully loaded, ensuring the theme
 * is applied before any components render.
 * @param company - The company resolved during bootstrap
 */
export const getCompanyConfigApplicationProvider = (company: Company) => {
  return [
    { provide: COMPANY, useValue: company },
    {
      provide: APP_INITIALIZER,
      useFactory: (companyThemeLoaderService: CompanyThemeLoaderService) => () => companyThemeLoaderService.initialize(),
      deps: [CompanyThemeLoaderService],
      multi: true,
    },
  ];
};

/**
 * It gets the application provider for the app initializer. In the factory method it will
 * call to the initialize method of the AppInitializerService. Where the logic about loading
 * the sharedStore, loading the data from deep links and initialize the QTLiteStore is done.
 * With this, all the data will be loaded before the application starts.
 */
export const getAppInitializers = () => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializerService: AppInitializerService) => () => appInitializerService.initialize(),
      deps: [AppInitializerService],
      multi: true,
    },
  ];
};

/**
 * It gets the application provider for the store orchestrator.
 * This is done to control the initialization of the orchestration logic.
 */
export const getStoreOrchestratorApplicationProvider = () => {
  return [
    StoreOrchestratorService,
    {
      provide: APP_INITIALIZER,
      useFactory: (orchestrator: StoreOrchestratorService) => () => orchestrator.initOrchestration(),
      deps: [StoreOrchestratorService],
      multi: true,
    },
  ];
};

export const getHostDataReaderApplicationProvider = () => {
  return [
    HostDataReaderService,
    {
      provide: APP_INITIALIZER,
      useFactory: (hostDataReaderService: HostDataReaderService) => () => hostDataReaderService.initializeQueryParameters(),
      deps: [HostDataReaderService],
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for the view page tracking service. It will start tracking the
 * view page events when the application is initialized.
 */
export const getViewPageTrackingService = () => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (viewPageTrackingService: ViewPageTrackingService) => () => viewPageTrackingService.startTrackingViewPageEvents(),
      deps: [ViewPageTrackingService],
      multi: true,
    },
  ];
};

/**
 * Gets the application provider for the view page tracking service. It will start tracking the
 * view page events when the application is initialized.
 */
export const getUpdatedQuoteTrackingService = () => {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: (updatedQuoteTrackingService: UpdatedQuoteTrackingService) => () => updatedQuoteTrackingService.startTrackingUpdatedQuoteEvent(),
      deps: [UpdatedQuoteTrackingService, DATA_LAYER_SERVICE],
      multi: true,
    },
  ];
};

export const getDataLayerProviders = () => {
  return [
    {
      provide: DATA_LAYER_SERVICE,
      useClass: QuoteToolLiteGoogleAnalyticsDataLayerService,
    },
    provideDataLayerStrategies(),
  ];
};

const provideDataLayerStrategies = () => {
  return [AdultQuoteDataLayerStrategy, JuniorQuoteDataLayerStrategy, OnlineQuoteDataLayerStrategy, AyaQuoteDataLayerStrategy].reduce(
    (providers: Provider[], strategyClass: Type<QuoteDataLayerStrategy>) => {
      providers.push(provideDataLayerStrategy(strategyClass));
      return providers;
    },
    [] as Provider[],
  );
};

const provideDataLayerStrategy = <T extends QuoteDataLayerStrategy>(strategyClass: Type<T>): Provider => ({
  provide: QUOTE_DATA_LAYER_STRATEGIES,
  useFactory: (strategy: T) => {
    strategy.initializeDataLayerStepsBuilders();
    return strategy;
  },
  deps: [strategyClass],
  multi: true,
});
